import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { memo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFieldName, useSchemaForm } from "../context";
import { buildDefault } from "../defaults";
import { UnionNode, ValueType } from "../types";
import { FieldLabel } from "./FieldLabel";
import { FieldBody } from "./FieldRenderer";

const hasType = (value: unknown, type: ValueType) => {
  switch (type) {
    case "array":
      return Array.isArray(value);
    case "object":
      return (
        typeof value === "object" && value !== null && !Array.isArray(value)
      );
    case "integer":
      return Number.isInteger(value);
    default:
      return typeof value === type;
  }
};

/*
 * A value of one of several types (e.g. instructions as one text or as a list of texts).
 * A toggle for the type, then the field of that type. The value of the other type is kept aside.
 */
export const UnionField = memo(({ node }: { node: UnionNode }) => {
  const { t } = useTranslation();
  const { valueCache } = useSchemaForm();
  const { control, setValue } = useFormContext();
  const name = useFieldName(node.path);
  const value = useWatch({ control, name });

  const selected =
    node.options.find((option) => hasType(value, option.type)) ??
    node.options[0];

  const select = (type: ValueType) => {
    const option = node.options.find((item) => item.type === type);
    if (!option || option === selected) return;

    valueCache.current[`${name}.${selected.type}`] = value;

    setValue(
      name,
      valueCache.current[`${name}.${type}`] ?? buildDefault(option.node, false),
      { shouldDirty: true },
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <FieldLabel
        title={node.title}
        description={node.description}
        required={node.required}
      />

      <ToggleButtonGroup
        value={selected.type}
        exclusive
        size="small"
        onChange={(_, next: ValueType | null) => {
          if (next) select(next);
        }}
        aria-label={t("schemaForm.typeLabel", { name: node.title })}
        sx={{ mb: 1, "& .MuiToggleButton-root": { textTransform: "none" } }}
      >
        {node.options.map((option) => (
          <ToggleButton key={option.type} value={option.type}>
            {t(`schemaForm.valueType.${option.type}`)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <FieldBody key={selected.type} node={selected.node} bare />
    </Box>
  );
});

UnionField.displayName = "UnionField";
